import React from "react";
import { motion } from "framer-motion";
import { CheckCircle2, ChevronDown } from "lucide-react";

const fadeUp = {
  hidden: { opacity: 0, y: 16 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.55, ease: "easeOut", delay: i * 0.08 },
  }),
};

// Only list facts that are true today. Add AMFI ARN / IRDAI / TNRERA
// numbers here once the registrations are actually granted.
const trustItems = [
  "9 free financial calculators",
  "No signup required",
  "Based in Chennai",
];

const metrics = [
  { label: "Calculators", value: "9" },
  { label: "Service Areas", value: "5" },
  { label: "Est.", value: "2026" },
];

const GrowthChart = () => (
  <svg
    viewBox="0 0 400 240"
    className="w-full h-auto"
    role="img"
    aria-label="Financial growth chart"
  >
    {/* Dashed horizontal grid */}
    {[40, 90, 140, 190].map((y) => (
      <line
        key={y}
        x1="10"
        y1={y}
        x2="390"
        y2={y}
        stroke="rgba(255,255,255,0.05)"
        strokeDasharray="4 6"
        strokeWidth="1"
      />
    ))}
    {/* Area fill under curve */}
    <path
      d="M10,200 C70,180 110,150 160,140 C210,130 250,90 300,70 C340,55 370,40 390,30 L390,210 L10,210 Z"
      fill="rgba(29,158,117,0.10)"
    />
    {/* Curve */}
    <path
      d="M10,200 C70,180 110,150 160,140 C210,130 250,90 300,70 C340,55 370,40 390,30"
      fill="none"
      stroke="#1D9E75"
      strokeWidth="2.5"
      strokeLinecap="round"
    />
    {/* Points */}
    {[
      [70, 180],
      [160, 140],
      [250, 90],
      [340, 50],
      [390, 30],
    ].map(([cx, cy], i) => (
      <g key={i}>
        <circle cx={cx} cy={cy} r="8" fill="rgba(29,158,117,0.18)" />
        <circle cx={cx} cy={cy} r="4.5" fill="#1D9E75" />
      </g>
    ))}
  </svg>
);

export const Hero = () => {
  return (
    <section
      id="home"
      className="relative w-full overflow-hidden"
      style={{
        backgroundColor: "#0D1117",
        minHeight: "100vh",
      }}
      data-testid="hero-section"
    >
      {/* Grid texture */}
      <div className="absolute inset-0 bg-hero-grid pointer-events-none" />
      {/* Soft radial glow */}
      <div
        className="absolute -top-40 -left-40 w-[520px] h-[520px] rounded-full pointer-events-none"
        style={{
          background:
            "radial-gradient(circle, rgba(29,158,117,0.18) 0%, rgba(13,17,23,0) 70%)",
        }}
      />

      <div className="relative max-w-7xl mx-auto px-5 sm:px-8 pt-32 pb-24 md:pt-36 md:pb-32 min-h-screen flex items-center">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 lg:gap-16 w-full items-center">
          {/* Left 60% */}
          <div className="lg:col-span-3">
            <motion.p
              initial="hidden"
              animate="show"
              variants={fadeUp}
              custom={0}
              className="uppercase font-semibold"
              style={{
                color: "#1D9E75",
                fontSize: 12,
                letterSpacing: "0.15em",
              }}
              data-testid="hero-eyebrow"
            >
              Financial Services &amp; Technology
            </motion.p>

            <motion.h1
              initial="hidden"
              animate="show"
              variants={fadeUp}
              custom={1}
              className="font-display text-white mt-5 font-bold"
              style={{
                fontSize: "clamp(38px, 6vw, 56px)",
                lineHeight: 1.1,
                letterSpacing: "-0.02em",
              }}
              data-testid="hero-headline"
            >
              Build Wealth.<br />
              <span
                style={{
                  borderBottom: "2px solid #1D9E75",
                  paddingBottom: "0.05em",
                }}
              >
                Intelligently.
              </span>
            </motion.h1>

            <motion.p
              initial="hidden"
              animate="show"
              variants={fadeUp}
              custom={2}
              className="mt-6"
              style={{
                color: "#718096",
                fontSize: 18,
                maxWidth: 520,
                lineHeight: 1.6,
              }}
              data-testid="hero-subheading"
            >
              Meta Monk Ventures is your one-stop financial growth partner - from SIP
              investments and insurance to smart loan planning and technology solutions.
            </motion.p>

            <motion.div
              initial="hidden"
              animate="show"
              variants={fadeUp}
              custom={3}
              className="mt-8 flex flex-wrap items-center gap-3"
            >
              <a
                href="#calculator"
                className="inline-flex items-center justify-center rounded-lg font-semibold text-white transition-colors"
                style={{
                  backgroundColor: "#1D9E75",
                  fontSize: 14,
                  padding: "12px 24px",
                }}
                onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "#0F6E56")}
                onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "#1D9E75")}
                data-testid="hero-cta-calculator"
              >
                Try Our Calculators
              </a>
              <a
                href="#services"
                className="inline-flex items-center justify-center rounded-lg font-semibold text-white transition-colors"
                style={{
                  backgroundColor: "transparent",
                  border: "1px solid rgba(255,255,255,0.2)",
                  fontSize: 14,
                  padding: "12px 24px",
                }}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.backgroundColor = "rgba(255,255,255,0.06)")
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.backgroundColor = "transparent")
                }
                data-testid="hero-cta-services"
              >
                Explore Services
              </a>
            </motion.div>

            <motion.div
              initial="hidden"
              animate="show"
              variants={fadeUp}
              custom={4}
              className="mt-8 flex flex-wrap gap-x-6 gap-y-3"
              data-testid="hero-trust-row"
            >
              {trustItems.map((t) => (
                <div key={t} className="flex items-center gap-2">
                  <CheckCircle2 size={14} color="#1D9E75" />
                  <span style={{ color: "#718096", fontSize: 13 }}>{t}</span>
                </div>
              ))}
            </motion.div>
          </div>

          {/* Right 40% */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.7, ease: "easeOut", delay: 0.2 }}
            className="hidden lg:block lg:col-span-2"
          >
            <div
              className="rounded-2xl p-5"
              style={{
                backgroundColor: "rgba(22,27,34,0.6)",
                border: "1px solid rgba(255,255,255,0.06)",
                backdropFilter: "blur(8px)",
              }}
            >
              <GrowthChart />
            </div>

            <div className="grid grid-cols-3 gap-3 mt-4">
              {metrics.map((m) => (
                <div
                  key={m.label}
                  className="rounded-xl p-4 min-w-0"
                  style={{
                    backgroundColor: "#161B22",
                    border: "1px solid rgba(255,255,255,0.08)",
                  }}
                  data-testid={`hero-metric-${m.label.toLowerCase().replace(/\./g, "").replace(/\s+/g, "-")}`}
                >
                  <div style={{ color: "#718096", fontSize: 11 }}>{m.label}</div>
                  <div
                    className="font-mono-fin font-semibold text-white mt-1 truncate"
                    style={{ fontSize: "clamp(14px, 1.3vw, 20px)", lineHeight: 1.15 }}
                  >
                    {m.value}
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 hidden md:block">
        <ChevronDown
          size={22}
          color="rgba(255,255,255,0.3)"
          className="animate-mm-bounce"
        />
      </div>
    </section>
  );
};
